import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../images/first page/cropped-exosomes_transperant.png';
import '../App.css';

const Navbar = ({ language = 'bg', onLanguageChange }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();

  // Check if mobile on mount and when resizing
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Initial check
    checkIfMobile();
    
    // Add resize listener
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Handle scroll event to change navbar background
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 3) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle mobile menu click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuOpen && !event.target.closest('.mobile-menu-container') && !event.target.closest('.hamburger-menu')) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileMenuOpen]);

  // Prevent scroll when menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  // Handle language toggle
  const toggleLanguage = () => {
    if (onLanguageChange) {
      onLanguageChange(language === 'bg' ? 'en' : 'bg');
    }
  };

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Navigation items
  const navItems = [
    { path: '/', label: language === 'bg' ? 'Начало' : 'Home' },
    { path: '/about', label: language === 'bg' ? 'За нас' : 'About Us' },
    { path: '/products', label: language === 'bg' ? 'Продукти' : 'Products' },
    { path: '/mscs', label: language === 'bg' ? 'Мезенхимни стволови клетки' : 'MSCs' },
    { path: '/contact', label: language === 'bg' ? 'Контакт' : 'Contact' },
  ];

  // Background style for navbar that's different for mobile and desktop
  const navbarBackgroundStyle = isMobile ? 
    {
      backgroundColor: scrolled ? '#000000' : 'rgba(0, 0, 0, 0.7)',
      boxShadow: scrolled ? '0 2px 10px rgba(0, 0, 0, 0.4)' : 'none',
      borderBottom: scrolled ? '1px solid rgba(124, 58, 237, 0.1)' : 'none'
    } : {};

  // Mobile content positioning style
  const mobileContentStyle = isMobile ? { paddingTop: '7px' } : {};

  return (
    <nav className={`main-navbar ${scrolled ? 'scrolled' : ''}`} style={navbarBackgroundStyle}>
      <div className="navbar-container" style={mobileContentStyle}>
        {/* Mobile Hamburger Menu - MORE TO LEFT */}
        <button 
          className="hamburger-menu" 
          onClick={toggleMobileMenu}
          style={{
            display: 'none',
            position: 'absolute',
            left: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '40px',
            height: '40px',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '6px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            zIndex: 1000,
            padding: 0
          }}
        >
          <span style={{
            display: 'block',
            width: '24px',
            height: '3px',
            backgroundColor: 'white',
            transition: 'all 0.3s ease',
            transform: mobileMenuOpen ? 'translateY(9px) rotate(45deg)' : 'none'
          }}></span>
          <span style={{
            display: 'block',
            width: '24px',
            height: '3px',
            backgroundColor: 'white',
            transition: 'all 0.3s ease',
            opacity: mobileMenuOpen ? 0 : 1
          }}></span>
          <span style={{
            display: 'block',
            width: '24px',
            height: '3px',
            backgroundColor: 'white',
            transition: 'all 0.3s ease',
            transform: mobileMenuOpen ? 'translateY(-9px) rotate(-45deg)' : 'none'
          }}></span>
        </button>

        {/* Logo */}
        <Link to="/" className="navbar-logo">
          <img src={logo} alt="EXOREVIVE" />
          <span>EXOREVIVE</span>
        </Link>

        {/* Desktop Navigation Links */}
        <div className="nav-links">
          {navItems.map((item, index) => (
            <Link
              key={index}
              to={item.path}
              className={location.pathname === item.path ? 'active' : ''}
            >
              {item.label}
            </Link>
          ))}
        </div>

        {/* Language Toggle - Desktop Version */}
        <div className="language-toggle desktop-language-toggle">
          <button onClick={toggleLanguage} aria-label="Change language">
            <div className="language-switch">
              <div className={`language-option ${language === 'bg' ? 'active' : 'inactive'}`}>БГ</div>
              <div className={`language-option ${language === 'en' ? 'active' : 'inactive'}`}>EN</div>
              <div className={`language-slider ${language}`}></div>
            </div>
          </button>
        </div>

        {/* Language Toggle - Mobile Version (simple text button) */}
        <button 
          onClick={toggleLanguage} 
          className="mobile-language-toggle"
          style={{
            display: 'none',
            position: 'absolute',
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'rgba(124, 58, 237, 0.2)',
            border: '1px solid rgba(124, 58, 237, 0.3)',
            borderRadius: '5px',
            color: 'white',
            padding: '6px 12px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            zIndex: 1000
          }}
        >
          {language === 'bg' ? 'EN' : 'БГ'}
        </button>

        {/* Mobile Menu */}
        <div 
          className="mobile-menu-container"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            zIndex: 999,
            display: mobileMenuOpen ? 'block' : 'none',
            opacity: mobileMenuOpen ? 1 : 0,
            transition: 'opacity 0.3s ease'
          }}
        >
          <div 
            className="mobile-menu"
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '80%',
              maxWidth: '300px',
              height: '100%',
              background: '#000000',
              display: 'flex',
              flexDirection: 'column',
              padding: '60px 20px 30px',
              transform: mobileMenuOpen ? 'translateX(0)' : 'translateX(100%)',
              transition: 'transform 0.3s ease'
            }}
          >
            <div style={{ marginBottom: '30px', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '20px' }}>
              <Link to="/" className="navbar-logo" style={{ justifyContent: 'center' }} onClick={() => setMobileMenuOpen(false)}>
                <img src={logo} alt="EXOREVIVE" style={{ height: '40px', marginRight: '10px' }} />
                <span style={{ color: 'white', fontSize: '1.2rem' }}>EXOREVIVE</span>
              </Link>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {navItems.map((item, index) => (
                <Link
                  key={index}
                  to={item.path}
                  className={location.pathname === item.path ? 'active' : ''}
                  style={{
                    color: 'white',
                    textDecoration: 'none',
                    fontSize: '18px',
                    padding: '10px 15px',
                    borderRadius: '5px',
                    background: location.pathname === item.path ? 'rgba(124, 58, 237, 0.2)' : 'transparent'
                  }}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
            </div>
            <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'center', paddingTop: '20px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <button 
                onClick={toggleLanguage} 
                style={{
                  background: 'rgba(124, 58, 237, 0.2)',
                  border: '1px solid rgba(124, 58, 237, 0.3)',
                  borderRadius: '5px',
                  color: 'white',
                  padding: '8px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {language === 'bg' ? 'EN' : 'БГ'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile specific CSS */}
      <style jsx>{`
        @media (max-width: 768px) {
          .hamburger-menu {
            display: flex !important;
          }
          .nav-links {
            display: none;
          }
          .navbar-logo {
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
          }
          .navbar-container {
            justify-content: space-between;
            padding: 0 20px;
            position: relative;
          }
          .desktop-language-toggle {
            display: none !important;
          }
          .mobile-language-toggle {
            display: block !important;
          }
          /* Override any CSS classes that might be making the navbar transparent */
          .main-navbar.scrolled {
            background-color: #000000 !important;
            backdrop-filter: none !important;
          }
        }
        @media (max-width: 480px) {
          .main-navbar {
            padding-top: 32px !important;
            min-height: 64px !important;
            background-color: #000 !important;
            backdrop-filter: none !important;
            box-shadow: 0 2px 10px rgba(0,0,0,0.4) !important;
            border-bottom: 1px solid rgba(124, 58, 237, 0.1) !important;
          }
          .navbar-container {
            padding-top: 12px !important;
          }
          .navbar-logo span {
            color: #fff !important;
          }
          .nav-links a,
          .nav-links .active {
            color: #fff !important;
          }
        }
      `}</style>
    </nav>
  );
};

export default Navbar; 